import React from "react";

import { Grid } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";

import { faPencil } from "@fortawesome/pro-solid-svg-icons";

import { EContractAgreementAction } from "actions";

import IconTextButton from "components/Button/IconTextButton";
import { MainContentWrapper } from "components/Layout";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import { Paper } from "components/Paper";
import { BoldText, TitleText } from "components/Text";

import { ApiModel } from "constants/loading";

import {
  getActiveLivestockAgentDeployment,
  selectEContractAgreementsInCurrentSaleListSorted,
  selectSaleLotIdsWithoutEContractAgreement,
} from "selectors";

import { useHasDeploymentPermission } from "hooks";
import { DeploymentPermissions } from "constants/permissions";
import { getIsOnline } from "selectors/root";

import { EContractAgreementSummaryCard } from "./eContractCard";
import { ECONTRACT_TYPES_WITH_SPECIAL_CONDITIONS } from "constants/eContract";

export const EContractView = (): React.JSX.Element => {
  const agreements = useSelector(
    selectEContractAgreementsInCurrentSaleListSorted,
  );
  const saleLotIdsWithoutAgreement = useSelector(
    selectSaleLotIdsWithoutEContractAgreement,
  );

  const dispatch = useDispatch();
  const isOnline = useSelector(getIsOnline);
  const canCreateEContract = useHasDeploymentPermission(
    DeploymentPermissions.canCreateEContract,
  );

  const deployment = useSelector(getActiveLivestockAgentDeployment);

  const onGenerate = () => {
    dispatch(EContractAgreementAction.generate());
  };

  const eContractType = deployment?.deploymentSettings?.eContractType || null;

  const eContractHandlesSpecialConditions =
    ECONTRACT_TYPES_WITH_SPECIAL_CONDITIONS.includes(eContractType);

  return (
    <WaitForSync
      requiredData={[
        ApiModel.BUSINESSES,
        ApiModel.CONSIGNMENTS,
        ApiModel.DEPLOYMENTS,
        ApiModel.ECONTRACT_AGREEMENTS,
        ApiModel.ECONTRACT_SPECIAL_CONDITIONS,
        ApiModel.ECONTRACTS,
        ApiModel.LIVESTOCK_AGENTS,
      ]}
      subjectName="eContract agreements"
    >
      <MainContentWrapper>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Paper elevation={1} backgroundColor="grayF3">
              <TitleText block>eContracts</TitleText>
              <p>
                eContracts use the data you&apos;ve already added.
                <br />
                To modify content of an eContract, modify Sale, Lot or
                Consignment data then void any existing contracts, and click
                Draft New{" "}
                {eContractHandlesSpecialConditions
                  ? "to apply any special conditions and"
                  : "to"}{" "}
                generate the new eContract.
              </p>
              <Grid container justifyContent="flex-end">
                <IconTextButton
                  disabled={
                    !isOnline ||
                    !canCreateEContract ||
                    saleLotIdsWithoutAgreement.length === 0
                  }
                  icon={faPencil}
                  onClick={onGenerate}
                >
                  <BoldText>
                    Generate eContract Agreements for{" "}
                    {saleLotIdsWithoutAgreement.length} Lots
                  </BoldText>
                </IconTextButton>
              </Grid>
            </Paper>
          </Grid>

          {agreements.map(agreement => {
            return (
              <Grid item xs={12} key={agreement.id}>
                <EContractAgreementSummaryCard
                  agreementId={agreement.id}
                  generateContractHandler={() =>
                    dispatch(
                      EContractAgreementAction.generateContract({
                        id: agreement.id,
                      }),
                    )
                  }
                  eContractAgreementHandlesSpecialConditions={
                    eContractHandlesSpecialConditions
                  }
                />
              </Grid>
            );
          })}
        </Grid>
      </MainContentWrapper>
    </WaitForSync>
  );
};
